import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import React, { useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enUS } from "date-fns/locale";
import {
  DateValidationError,
  LocalizationProvider,
  PickersLayout,
} from "@mui/x-date-pickers";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import styled from "styled-components";
import UpdatedBadge from "./UpdatedBadge";
import {
  Box,
  createTheme,
  FormHelperText,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { format, parse } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import {
  answersActions,
  RootStateAnswers,
} from "../../../questionnaire/store/answers-slice";

type Props = {
  label: string;
  defaultValue: Date;
  minValue?: Date;
  maxValue?: Date;
  category: string;
  setOpenPopperId: React.Dispatch<React.SetStateAction<string | null>>;
};

interface StyledDatePickerWrapperProps {
  theme: any;
  input: Date | null;
}

const StyledDatePickerWrapper = styled(Box)<StyledDatePickerWrapperProps>(
  ({ theme, input }) => ({
    position: "relative",
    "& .MuiFormControl-root": {
      width: "100%",
    },
    ".MuiOutlinedInput-root": {
      borderRadius: "8px",
      height: "56px",
      padding: "24px 12px 8px",
      display: "flex",
      alignItems: "center",
      fontFamily: "var(--font-Medium)",
      fontSize: "12px",
    },

    ".MuiOutlinedInput-notchedOutline": {
      borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
    },

    ".Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.colors_ds.PRIMARY_ICON_MAIN + "!important",
    },

    ".Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.colors_ds.DANGER_GRAPH_DEFICIT,
    },

    ".MuiOutlinedInput-input": {
      padding: "0",
    },

    ".MuiFormLabel-root": {
      fontFamily: "var(--font-Medium)",
      fontSize: "10px",
      color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
      transform:
        input !== null
          ? "translate(12px, 10px) scale(1)"
          : "translate(12px, 18px) scale(1)",
    },

    ".Mui-focused.MuiFormLabel-root": {
      color: theme.colors_ds.PRIMARY_ICON_MAIN,
    },

    ".MuiFormHelperText-root": {
      fontFamily: "var(--font-Medium)",
      fontSize: "10px",
      color: theme.colors_ds.DANGER_GRAPH_DEFICIT,
      position: "absolute",
      bottom: "-16px",
      left: "-8px",
    },

    legend: {
      display: "none",
    },

    fieldset: {
      top: "0",
    },

    ".MuiButtonBase-root": {
      backgroundColor: "transparent",
      marginTop: "-12px",
    },
  })
);

const CustomDatePicker = ({
  label,
  defaultValue,
  minValue,
  maxValue,
  category,
  setOpenPopperId,
}: Props) => {
  const theme = useTheme();
  const [input, setInput] = useState<Date>(defaultValue);
  const [error, setError] = React.useState<DateValidationError | null>(null);
  const dispatch = useDispatch();

  const answers = useSelector((state: RootStateAnswers) => state.answers);

  const isUpdated =
    answers.biometric_modified_fields.indexOf(category) !== -1;

  const errorMessage = React.useMemo(() => {
    switch (error) {
      case "maxDate": {
        return "You are too young!";
      }
      case "minDate": {
        return "You are too old!";
      }
      case "invalidDate": {
        return "Invalid date";
      }
      default: {
        return "";
      }
    }
  }, [error]);

  const baseTheme = createTheme();

  const newTheme = createTheme({
    ...baseTheme,
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "8px",
            boxShadow: "var(--box-shadow-general)",
            marginTop: "4px",
            border: `1px solid ${theme.colors_ds.PRIMARY_ICON_MAIN}`,
          },
        },
      },
      // @ts-ignore
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
          },
          label: {
            fontFamily: "var(--font-Bold)",
          },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          spacer: {
            width: "2px",
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&[aria-colindex='6']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
            "&[aria-colindex='7']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
            "&[aria-selected='true']": {
              color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
              fontFamily: "var(--font-Bold)",
            },
          },
          today: {
            borderColor: "transparent",
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            fontFamily: "var(--font-Bold)",
            "&[aria-label='Saturday']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
            "&[aria-label='Sunday']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
          },
          weekContainer: {
            fontFamily: "var(--font-Bold)",
          },
        },
      },
      MuiPickersYear: {
        styleOverrides: {
          yearButton: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&.Mui-selected": {
              fontFamily: "var(--font-Bold)",
              fontSize: "12px",
              borderRadius: "8px",
            },
            "&.Mui-disabled": {
              opacity: 0.6,
            },
            "&:hover": {
              borderRadius: "8px",
            },
          },
        },
      },
      MuiPickersMonth: {
        styleOverrides: {
          monthButton: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&.Mui-selected": {
              fontFamily: "var(--font-Bold)",
              fontSize: "12px",
              borderRadius: "8px",
            },
            "&.Mui-disabled": {
              opacity: 0.6,
            },
            "&:hover": {
              borderRadius: "8px",
            },
          },
        },
      },
    },
  });

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={{ ...enUS, options: { weekStartsOn: 1 } }}
    >
      <ThemeProvider theme={newTheme}>
        <StyledDatePickerWrapper theme={theme} input={input}>
          <DatePicker
            views={["year", "month", "day"]}
            value={new Date(answers.biometricQuest.myAnswers[category])}
            disableHighlightToday={false}
            disablePast={false}
            label={
              isUpdated ? (
                <>
                  {label}
                  <UpdatedBadge />
                </>
              ) : (
                label
              )
            }
            yearsOrder="desc"
            maxDate={maxValue}
            minDate={minValue}
            onError={(newError) => setError(newError)}
            onChange={(newValue) => {
              if (newValue !== null) {
                setOpenPopperId(null);
                const formatValue = format(newValue, "yyyy-MM-dd");
                if (formatValue === answers.biometricOldAnswers[category]) {
                  if (
                    answers.biometric_modified_fields.indexOf(category) !== -1
                  ) {
                    dispatch(
                      answersActions.removeBiometricModifiedFields(category)
                    );
                  }
                } else {
                  if (
                    answers.biometric_modified_fields.indexOf(category) === -1
                  ) {
                    dispatch(
                      answersActions.addBiometricModifiedFields(category)
                    );
                  }
                }

                dispatch(
                  answersActions.updateBiometricAnswers({
                    [category]: formatValue,
                  })
                );
              }
            }}
            openTo={"day"}
            slots={{
              previousIconButton: KeyboardArrowLeftRoundedIcon,
              nextIconButton: KeyboardArrowRightRoundedIcon,
              switchViewIcon: KeyboardArrowDownRoundedIcon,
            }}
            slotProps={{
              textField: {
                placeholder: "MMM DD YYYY",
                helperText: errorMessage,
                inputProps: {
                  readOnly: true,
                },
              },
              switchViewIcon: {
                sx: {
                  width: "24px",
                  height: "24px",
                  fontSize: "2rem",
                  padding: 0,
                  cursor: "pointer",
                  borderRadius: "50%",
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                },
              },
              previousIconButton: {
                sx: {
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "50%",
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                  "&:hover": {
                    backgroundColor: theme.colorTokens.N[30],
                  },
                },
              },
              nextIconButton: {
                sx: {
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "50%",
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                  "&:hover": {
                    backgroundColor: theme.colorTokens.N[30],
                  },
                },
              },
            }}
            format="MMM dd yyyy" // Use format here
          />
        </StyledDatePickerWrapper>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default CustomDatePicker;
